import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateCalendarComponent } from './date-calendar.component';
import {
  buildRangedTimeOptions,
  currentDisplayDate,
  isValidTime,
  normalizeDisplayDate,
  parseDateTimeValue,
  toIsoDate,
} from './date-time-field.util';

@Component({
  selector: 'lib-date-time-field',
  standalone: true,
  imports: [DateCalendarComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeFieldComponent),
      multi: true,
    },
  ],
  template: `
    <div class="date-time-field">
      <div class="date-time-field__date-wrap">
        <input
          [id]="dateInputId"
          class="date-time-field__date"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          [attr.aria-label]="dateAriaLabel"
          [placeholder]="datePlaceholder"
          [value]="dateText"
          [disabled]="isDisabled"
          (input)="onDateInput($event)"
          (blur)="onDateBlur()"
        />
        <button
          type="button"
          class="date-time-field__date-toggle"
          aria-label="Open calendar"
          [disabled]="isDisabled"
          (click)="toggleCalendar()"
        >
          <span class="date-time-field__date-icon" aria-hidden="true"></span>
        </button>
        @if (isCalendarOpen) {
          <div class="date-time-field__date-popover">
            <lib-date-calendar
              [selectedDate]="selectedIsoDate"
              (selectedDateChange)="selectDate($event)"
            />
          </div>
        }
      </div>
      <select
        [id]="timeInputId"
        class="date-time-field__time"
        [attr.aria-label]="timeAriaLabel"
        [value]="timeText"
        [disabled]="isDisabled"
        (change)="onTimeChange($event)"
        (blur)="markAsTouched()"
      >
        <option value="">--:--</option>
        @for (option of timeOptions; track option) {
          <option [value]="option">{{ option }}</option>
        }
      </select>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    .date-time-field {
      display: grid;
      grid-template-columns: minmax(128px, 1fr) minmax(96px, 112px);
      gap: 8px;
      min-width: 0;
    }

    .date-time-field__date-wrap {
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 1fr) 40px;
      min-width: 0;
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #ffffff;
      box-sizing: border-box;
    }

    .date-time-field__date,
    .date-time-field__time {
      width: 100%;
      height: 40px;
      border-radius: 8px;
      padding: 0 12px;
      font: inherit;
      color: #111827;
      background: #ffffff;
      box-sizing: border-box;
    }

    .date-time-field__date {
      min-width: 0;
      border: none;
      border-radius: 8px 0 0 8px;
      background: transparent;
      outline: none;
    }

    .date-time-field__time {
      border: 1px solid #d1d5db;
    }

    .date-time-field__date-toggle {
      display: grid;
      place-items: center;
      border: none;
      border-left: 1px solid #e5e7eb;
      border-radius: 0 8px 8px 0;
      background: #f8fafc;
      color: #475569;
      cursor: pointer;
    }

    .date-time-field__date-icon {
      position: relative;
      width: 15px;
      height: 14px;
      border: 1.5px solid currentColor;
      border-radius: 3px;
      box-sizing: border-box;
    }

    .date-time-field__date-icon::before {
      content: '';
      position: absolute;
      top: 3px;
      left: -1.5px;
      right: -1.5px;
      border-top: 1.5px solid currentColor;
    }

    .date-time-field__date:disabled,
    .date-time-field__date-toggle:disabled,
    .date-time-field__time:disabled {
      opacity: 0.65;
      cursor: default;
    }

    .date-time-field__date-popover {
      position: absolute;
      z-index: 40;
      top: calc(100% + 6px);
      left: 0;
    }

    @media (max-width: 520px) {
      .date-time-field {
        grid-template-columns: minmax(0, 1fr);
      }
    }
  `],
})
export class DateTimeFieldComponent implements ControlValueAccessor, OnChanges {
  @Input() public inputId = `date-time-field-${Math.random().toString(36).slice(2, 10)}`;
  @Input() public datePlaceholder = 'dd.mm.yyyy';
  @Input() public dateAriaLabel = 'Date';
  @Input() public timeAriaLabel = 'Time';
  @Input() public defaultToToday = false;
  @Input() public minTime: string | null = null;
  @Input() public maxTime: string | null = null;
  @Input() public dateDraft: string | null = null;
  @Input() public timeDraft: string | null = null;
  @Input() public set timeStepMinutes(value: number) {
    const normalized = Math.trunc(value);
    this.timeStep = normalized > 0 && normalized <= 60 ? normalized : 15;
    this.baseTimeOptions = this.buildTimeOptions();
  }

  @Output() public readonly datePartChange = new EventEmitter<string>();
  @Output() public readonly timePartChange = new EventEmitter<string>();

  protected dateText = '';
  protected timeText = '';
  protected isDisabled = false;
  protected isCalendarOpen = false;
  protected timeStep = 15;
  protected baseTimeOptions = this.buildTimeOptions();

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected get dateInputId(): string {
    return `${this.inputId}-date`;
  }

  protected get timeInputId(): string {
    return `${this.inputId}-time`;
  }

  protected get selectedIsoDate(): string {
    return toIsoDate(this.dateText) ?? '';
  }

  protected get timeOptions(): string[] {
    const filtered = this.baseTimeOptions.filter((option) => this.isTimeAllowed(option));

    if (!this.timeText || filtered.includes(this.timeText)) {
      return filtered;
    }

    return [...filtered, this.timeText].sort();
  }

  public writeValue(value: string | null | undefined): void {
    const parsed = parseDateTimeValue(value);
    this.dateText = parsed.dateText || (this.defaultToToday ? currentDisplayDate() : '');
    this.timeText = parsed.timeText;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['minTime'] || changes['maxTime']) {
      this.baseTimeOptions = this.buildTimeOptions();
    }

    if (changes['defaultToToday'] && this.defaultToToday && !this.dateText.trim()) {
      this.dateText = currentDisplayDate();
    }

    if (changes['dateDraft'] && this.dateDraft) {
      const [year, month, day] = this.dateDraft.split('-');
      if (year && month && day) {
        this.dateText = `${day}.${month}.${year}`;
      }
    }

    if (changes['timeDraft'] && this.timeDraft) {
      this.timeText = this.timeDraft;
    }
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  protected onDateInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.dateText = target?.value ?? '';
    this.emitValue();
  }

  protected onDateBlur(): void {
    this.dateText = this.normalizeDateText(this.dateText);
    this.markAsTouched();
    this.emitDatePart();
    this.emitValue();
  }

  protected onTimeChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    this.timeText = target?.value ?? '';
    this.markAsTouched();
    if (isValidTime(this.timeText)) {
      this.timePartChange.emit(this.timeText);
    }
    this.emitValue();
  }

  protected markAsTouched(): void {
    this.onTouched();
  }

  protected toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
  }

  protected selectDate(value: string): void {
    const [year, month, day] = value.split('-');
    this.dateText = `${day}.${month}.${year}`;
    this.isCalendarOpen = false;
    this.markAsTouched();
    this.datePartChange.emit(value);
    this.emitValue();
  }

  private emitValue(): void {
    const isoDate = toIsoDate(this.dateText);

    if (!isoDate || !isValidTime(this.timeText)) {
      this.onChange('');
      return;
    }

    this.onChange(`${isoDate}T${this.timeText}`);
  }

  private normalizeDateText(value: string): string {
    return normalizeDisplayDate(value);
  }

  private emitDatePart(): void {
    const isoDate = toIsoDate(this.dateText);
    if (isoDate) {
      this.datePartChange.emit(isoDate);
    }
  }

  private isTimeAllowed(value: string): boolean {
    if (this.minTime && value < this.minTime) {
      return false;
    }

    if (this.maxTime && value > this.maxTime) {
      return false;
    }

    return true;
  }

  private buildTimeOptions(): string[] {
    return buildRangedTimeOptions(this.timeStep, this.minTime, this.maxTime);
  }
}
